import { defa } from 'defa-object';

/**
 * defa.views.UserCreateForm
 * A view to create a user, interlocked with login and forgot password.
 * @version 1.2.0
 */
import Form     from 'scripts/poppet/form';
import template from 'templates/userCreateForm.jst.html';

const selectors      = {
	html            : 'html',
	inputEmail      : '[data-input=create-email]',
	inputPhoneNumber: '[data-input=create-phone-number]',
	inputPasswords  : '[data-input^=create-password-]',
	inputPassword1  : '[data-input=create-password-1]',
	inputPassword2  : '[data-input=create-password-2]',
	textPassword    : '[data-text=create-password]',
	buttonLogin     : '[data-click=user-login]',
	buttonForgot    : '[data-click=user-forgot]',
};
const UserCreateForm = Form.extend( {
	
	// ------------------------------------------------
	// Setup
	// ------------------------------------------------
	
	template         : template,
	noSuccessFeedback: true, // because we'll show the success-feedback in the thank-you view.
	
	ui: {
		email       : selectors.inputEmail,
		number      : selectors.inputPhoneNumber,
		passwords   : selectors.inputPasswords,
		password1   : selectors.inputPassword1,
		password2   : selectors.inputPassword2,
		textPassword: selectors.textPassword,
		login       : selectors.buttonLogin,
		forgot      : selectors.buttonForgot,
	},
	
	events: {
		'click @ui.login' : 'onClickLogin',
		'click @ui.forgot': 'onClickForgot',
	},
	
	templateContext: function () {
		return {
			email  : this.options.email,
			context: this.options.context || '',
		};
	},
	
	
	// ------------------------------------------------
	// Event Handlers
	// ------------------------------------------------
	
	/**
	 * Sets up ui-listeners on render.
	 * @void
	 */
	onRender: function () {
		
		let self = this;
		
		// setup our country variables
		let country   = defa.objects.languageSettings.country,
			countries = [ 'no', 'se', 'dk', 'fi', ];
		
		// add country to the top of the array and remove duplicates
		countries.unshift( country );
		countries = _.uniq( countries );
		
		// setup numbers
		self.ui.number.intlTelInput( {
			preferredCountries: countries,
			utilsScript       : '/assets/vendor/intl-tel-input-utils.min.js',
			autoPlaceholder   : 'off',
			separateDialCode  : true,
		} );
		
		// TODO: Move matching field validator to it's own behavior? ( this code is not dry, see
		// view-user-password-view.js ) email-addresses, passwords etc should be typed twice.
		let passwordListenerSetup = function ( p1, p2 ) {
			// turn off the "other" focus listener
			p2.off( 'focus' );
			
			// check on password matching length, if passwords are valid.
			p2.on( 'input', function () {
				
				if ( p1.val().trim().length === p2.val().trim().length ) {
					// turn off listeners that get to this point.
					p2.off( 'blur' ).off( 'input' );
					
					// from this point on, trigger on each input change.
					self.ui.passwords.on( 'input', function () {
						self.doPasswordFeedback();
					} );
					self.doPasswordFeedback();
				}
			} );
			
			// check on leaving password field, if passwords are valid.
			p2.one( 'blur', function () {
				// turn off listeners that get to this point.
				p2.off( 'input' );
				
				// from this point on, trigger on each input change.
				self.ui.passwords.on( 'input', function () {
					self.doPasswordFeedback();
				} );
				self.doPasswordFeedback();
			} );
		};
		// trigger which password field was visited first.
		self.ui.password1.one( 'focus', function () {
			passwordListenerSetup( self.ui.password1, self.ui.password2 );
		} );
		self.ui.password2.one( 'focus', function () {
			passwordListenerSetup( self.ui.password2, self.ui.password1 );
		} );
	},
	
	/**
	 * Triggers when a user claims he's forgotten his password.
	 * @void
	 */
	onClickForgot: function ( event ) {
		event.preventDefault();
		this.trigger( 'nav', 'user:forgot' );
	},
	
	/**
	 * Triggers when a user requests to log in.
	 * @void
	 */
	onClickLogin: function ( event ) {
		event.preventDefault();
		this.trigger( 'nav', 'user:login' );
	},
	
	
	// ------------------------------------------------
	// Other functions
	// ------------------------------------------------
	
	/**
	 * Adjusts feedback for matching passwords.
	 * @void
	 */
	doPasswordFeedback: function () {
		let passwords = this.ui.passwords,
			feedback  = this.ui.textPassword;
		
		if ( !this.validatePasswords() ) {
			this.disableSubmit();
			passwords.addClass( 'error' );
			feedback
				.removeClass( 'fade-out' )
				.removeClass( 'hidden' )
				.addClass( 'fade-in' );
		} else {
			passwords.removeClass( 'error' );
			feedback
				.removeClass( 'fade-in' )
				.addClass( 'fade-out' );
			
			setTimeout( function () {
				if ( feedback.hasClass( 'fade-out' ) ) {
					feedback.addClass( 'hidden' );
				}
			}, 1000 );
		}
	},
	
	/**
	 * Validates the password-fields.
	 * @return {bool} True if the passwords match, false if not.
	 */
	validatePasswords: function () {
		let password1 = this.ui.password1.val().trim(),
			password2 = this.ui.password2.val().trim();
		
		return ( password1 === password2 );
	},
	
	/**
	 * Validates the create form.
	 * @return {bool} True if the form validates, false if not.
	 */
	validate: function () {
		return this.validatePasswords();
	},
	
	/**
	 * Trigger all feedbackfunctions in the form.
	 * @void
	 */
	feedback: function () {
		this.doPasswordFeedback();
	},
	
	/**
	 * Submits the form.
	 * @void
	 */
	submit: function () {
		
		let prefix      = '+' + this.ui.number.intlTelInput( 'getSelectedCountryData' ).dialCode.trim(),
			number      = this.ui.number.intlTelInput( 'getNumber' ).replace( prefix, '' ).trim(),
			phoneNumber = ( number )
						  ? {
					description: 'Primary',
					prefix     : prefix,
					number     : number,
				}
						  : {};
		
		let form = {
			email               : this.ui.email.val().trim(),
			password            : this.ui.password1.val().trim(),
			transientPhoneNumber: phoneNumber,
		};
		
		if ( this.options.token ) {
			_.extend( form, { token: this.options.token } );
		}
		
		this.model.save( form );
	},
	
} );

export default UserCreateForm;